import { useEffect, useState } from 'react';
import api from '../api/axios';
import { Link } from 'react-router-dom';
import { Clock, ArrowRight, BookOpen, PlusCircle, TrendingUp, Home, Palette, Zap, Calendar } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState('todas');
  const { user } = useAuth();

  useEffect(() => {
    api.get('/posts')
      .then(res => {
        setPosts(res.data);
        
        // Extraer categorías únicas de los posts
        const uniqueCategories = [...new Set(res.data.map(post => post.category))];
        setCategories(['todas', ...uniqueCategories]);
        
        setLoading(false);
      })
      .catch(err => {
        console.error(err);
        setLoading(false);
      });
  }, []);

  // Filtrar posts por categoría
  const filteredPosts = activeCategory === 'todas' 
    ? posts 
    : posts.filter(post => post.category === activeCategory);

  const featuredPost = filteredPosts[0];
  const otherPosts = filteredPosts.slice(1);

  // Función para obtener icono de categoría
  const getCategoryIcon = (category) => {
    const icons = {
      'arquitectura': <Home size={12} />,
      'diseno': <Palette size={12} />,
      'inversion': <TrendingUp size={12} />,
      'tendencias': <Zap size={12} />,
      'lifestyle': '✨',
      'mercado': '🏢'
    };
    return icons[category] || <BookOpen size={12} />;
  };

  // Función para obtener nombre de categoría
  const getCategoryName = (category) => {
    const names = {
      'arquitectura': 'Arquitectura',
      'diseno': 'Diseño',
      'inversion': 'Inversión',
      'tendencias': 'Tendencias',
      'lifestyle': 'Lifestyle',
      'mercado': 'Mercado'
    };
    return names[category] || category;
  };

  // Calcular tiempo de lectura aproximado
  const calculateReadingTime = (content) => {
    const wordsPerMinute = 200;
    if (!content) return 3; // Valor por defecto
    const text = content.replace(/<[^>]*>/g, '');
    const words = text.split(/\s+/).length;
    return Math.max(1, Math.ceil(words / wordsPerMinute));
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="w-10 h-10 border-2 border-slate-100 border-t-slate-900 animate-spin rounded-full" />
    </div>
  );

  return (
    <div className="min-h-screen bg-white pt-28 pb-20 px-6">
      <div className="max-w-7xl mx-auto">
        {/* Encabezado Editorial */}
        <header className="mb-16 border-b border-slate-100 pb-12 text-center md:text-left relative">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
            <div>
              <span className="text-blue-600 text-[10px] font-black uppercase tracking-[0.4em] mb-4 block">
                VEstates Journal
              </span>
              <h1 className="text-5xl md:text-7xl font-serif italic text-slate-900 mb-6">
                Editorial <span className="font-sans not-italic font-light text-slate-400">&</span> Noticias
              </h1>
              <p className="text-slate-500 max-w-2xl text-lg font-light leading-relaxed">
                Perspectivas sobre el mercado inmobiliario global, diseño de interiores y el arte de vivir bien.
              </p>
            </div>
            
            {/* Botón para crear nuevo post (solo para usuarios autorizados) */}
            {user && (user.rol === 'superadmin' || user.rol === 'editor') && (
              <div className="md:self-start">
                <Link 
                  to="/blog/crear"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-slate-900 text-white text-xs font-bold uppercase tracking-[0.2em] rounded-full hover:bg-blue-700 transition-all shadow-lg shadow-slate-900/10 group"
                >
                  <PlusCircle size={16} className="group-hover:rotate-90 transition-transform" />
                  Nueva Entrada
                </Link>
                <p className="text-xs text-slate-400 mt-2 text-center md:text-right">
                  Solo para editores
                </p>
              </div>
            )}
          </div>

          {/* Filtros de Categorías */}
          <div className="flex flex-wrap gap-3 mt-12 pt-6 border-t border-slate-100">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold uppercase tracking-widest transition-all ${
                  activeCategory === category
                    ? 'bg-blue-600 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {category === 'todas' ? (
                  'Todas las categorías'
                ) : (
                  <>
                    {getCategoryIcon(category)}
                    {getCategoryName(category)}
                  </>
                )}
              </button>
            ))}
          </div>
        </header>

        {/* Post Destacado (Hero Post) */}
        {featuredPost && (
          <section className="mb-24 group">
            <Link to={`/blog/${featuredPost.slug}`} className="grid md:grid-cols-12 gap-10 items-center">
              <div className="md:col-span-7 overflow-hidden rounded-[40px] aspect-[16/9] bg-slate-100 relative">
                <img 
                  src={featuredPost.image || "https://images.unsplash.com/photo-1460317442991-0ec239f33649?auto=format&fit=crop&q=80&w=1200"} 
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000"
                  alt={featuredPost.title}
                />
                {/* Badge de categoría */}
                <div className="absolute top-6 left-6">
                  <span className="flex items-center gap-2 px-4 py-1.5 bg-white/90 backdrop-blur-sm rounded-full text-[10px] font-bold uppercase tracking-widest text-slate-900">
                    {getCategoryIcon(featuredPost.category)}
                    {getCategoryName(featuredPost.category)}
                  </span>
                </div>
              </div>
              <div className="md:col-span-5 space-y-6">
                <div className="flex items-center gap-4 text-[10px] font-bold uppercase tracking-widest text-blue-600">
                  <span className="px-3 py-1 bg-blue-50 rounded-full">Destacado</span>
                  <div className="w-1 h-1 bg-slate-300 rounded-full" />
                  <span className="text-slate-400">{new Date(featuredPost.created_at).toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })}</span>
                  <div className="w-1 h-1 bg-slate-300 rounded-full" />
                  <div className="flex items-center gap-1 text-slate-400">
                    <Clock size={10} /> {calculateReadingTime(featuredPost.content)} min
                  </div>
                </div>
                <h2 className="text-4xl md:text-5xl font-serif text-slate-900 group-hover:text-blue-700 transition-colors leading-tight">
                  {featuredPost.title}
                </h2>
                <p className="text-slate-500 text-lg font-light leading-relaxed line-clamp-3">
                  {featuredPost.excerpt || "Exploramos las tendencias que están redefiniendo el concepto de lujo residencial en las ciudades más vibrantes del mundo..."}
                </p>
                <div className="pt-4 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-slate-900 border-b-2 border-slate-900 pb-1 group-hover:text-blue-600 group-hover:border-blue-600 transition-all">
                  Leer artículo completo <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </Link>
          </section>
        )}

        {/* Cuadrícula de otros posts */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
          {otherPosts.map(post => (
            <article key={post.id} className="group flex flex-col">
              <Link to={`/blog/${post.slug}`} className="mb-6 overflow-hidden rounded-3xl aspect-[4/3] bg-slate-50 relative">
                <img 
                  src={post.image || "https://images.unsplash.com/photo-1484154218962-a197022b5858?auto=format&fit=crop&q=80&w=800"} 
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" 
                  alt={post.title}
                />
                {/* Badge de categoría */}
                <div className="absolute top-4 left-4">
                  <span className="flex items-center gap-1.5 px-3 py-1 bg-white/90 backdrop-blur-sm rounded-full text-[10px] font-bold uppercase tracking-widest text-slate-900">
                    {getCategoryIcon(post.category)}
                    {getCategoryName(post.category)}
                  </span>
                </div>
              </Link>
              <div className="space-y-4 flex-1">
                <div className="flex items-center gap-3 text-[10px] font-bold uppercase tracking-widest text-slate-400">
                  <div className="flex items-center gap-1">
                    <Calendar size={10} /> 
                    <span>{new Date(post.created_at).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}</span>
                  </div>
                  <div className="w-1 h-1 bg-slate-200 rounded-full" />
                  <div className="flex items-center gap-1">
                    <Clock size={10} /> {calculateReadingTime(post.content)} min
                  </div>
                </div>
                <h3 className="text-2xl font-serif text-slate-900 leading-snug group-hover:text-blue-700 transition-colors">
                  <Link to={`/blog/${post.slug}`}>{post.title}</Link>
                </h3>
                <p className="text-slate-500 text-sm font-light leading-relaxed line-clamp-2">
                  {post.excerpt || "Descubre las últimas tendencias y análisis del mercado inmobiliario de lujo..."}
                </p>
                <div className="pt-2">
                  <Link 
                    to={`/blog/${post.slug}`} 
                    className="inline-flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 group-hover:text-slate-900 transition-all"
                  >
                    <span>Continuar Leyendo</span>
                    <ArrowRight size={10} className="group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </div>
            </article>
          ))}
        </div>

        {filteredPosts.length === 0 && (
          <div className="py-24 text-center space-y-6">
            <div className="w-20 h-20 mx-auto rounded-full bg-slate-100 flex items-center justify-center">
              <BookOpen size={32} className="text-slate-300" />
            </div>
            <div>
              <p className="font-serif italic text-slate-400 text-2xl mb-2">No hay publicaciones aún</p>
              <p className="text-slate-500 max-w-md mx-auto">
                {activeCategory === 'todas' 
                  ? "Pronto publicaremos contenido exclusivo sobre el mundo inmobiliario."
                  : `No hay publicaciones en la categoría "${getCategoryName(activeCategory)}".`}
              </p>
              {user && (user.rol === 'superadmin' || user.rol === 'editor') && (
                <div className="mt-6">
                  <Link 
                    to="/blog/crear"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white text-xs font-bold uppercase tracking-[0.2em] rounded-full hover:bg-blue-700 transition-all"
                  >
                    <PlusCircle size={14} />
                    Crear la primera publicación
                  </Link>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Contador de posts */}
        {filteredPosts.length > 0 && (
          <div className="mt-20 pt-8 border-t border-slate-100">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="text-slate-400 text-sm">
                <span className="font-bold">{filteredPosts.length}</span> {filteredPosts.length === 1 ? 'artículo' : 'artículos'} 
                {activeCategory !== 'todas' && ` en ${getCategoryName(activeCategory).toLowerCase()}`}
              </div>
              
              {user && (user.rol === 'superadmin' || user.rol === 'editor') && (
                <Link 
                  to="/blog/crear"
                  className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-slate-900 hover:text-blue-600 transition-colors"
                >
                  <PlusCircle size={14} />
                  Agregar nueva entrada
                </Link>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Blog;